import { Box, Button, Card, TextField, Typography, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { api } from "../../services/api.service";
import { setUserInfo } from "../../store/userInfoSlice";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../router/constants";

export function EditUserProfileForm() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.userInfo);

  const [formData, setFormData] = useState({
    name: user.name || "",
    email: user.email || "",
    address: user.profile?.address || "",
    city: user.profile?.city || "",
    state: user.profile?.state || "",
    zip_code: user.profile?.zip_code || "",
    birthDay: user.profile?.birthDay || "",
    registrationNumber: user.profile?.registrationNumber || "",
    phone: user.profile?.phone || "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const applyMask = (value: string, maskType: "cpf" | "phone" | "cep") => {
    const numbers = value.replace(/\D/g, "");

    if (maskType === "cpf") {
      return numbers
        .slice(0, 11)
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }

    if (maskType === "phone") {
      return numbers
        .slice(0, 11)
        .replace(/(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2");
    }

    if (maskType === "cep") {
      return numbers
        .slice(0, 8)
        .replace(/(\d{5})(\d)/, "$1-$2");
    }

    return value;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let finalValue: string = value;

    if (name === "registrationNumber") {
      finalValue = applyMask(value, "cpf");
    } else if (name === "phone") {
      finalValue = applyMask(value, "phone");
    } else if (name === "zip_code") {
      finalValue = applyMask(value, "cep");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
  };

  const removeMask = (value: string) => {
    return value.replace(/\D/g, "");
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const dataToSend = {
        ...formData,
        registrationNumber: formData.registrationNumber ? removeMask(formData.registrationNumber) : "",
        phone: removeMask(formData.phone),
        zip_code: removeMask(formData.zip_code),
      };

      const { data } = await api.post(`/user/profile`, dataToSend);

      dispatch(
        setUserInfo({
          ...user,
          name: formData.name,
          email: formData.email,
          profile: data,
        })
      );

      toast.success("Perfil atualizado com sucesso!");

      navigate(ROUTES.HOME);
    } catch (error) {
      toast.error("Erro ao salvar perfil. Tente novamente.");
      console.error("Erro ao salvar perfil:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      padding={4}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "calc(100vh - 64px)",
      }}
    >
      <Card
        sx={{
          padding: 4,
          maxWidth: 800,
          width: "100%",
        }}
      >
        <Typography variant="h5" fontWeight={600} color="#505152ff" mb={1}>
          Editar Perfil
        </Typography>
        <Typography variant="body2" color="#8d8f92ff" mb={3}>
          Atualize suas informações pessoais quando precisar.
        </Typography>

        <Box display="flex" flexDirection="column" gap={3} width="100%">
          <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
            <TextField
              label="Nome"
              fullWidth
              name="name"
              value={formData.name}
              onChange={handleChange}
            />

            <TextField
              label="Email"
              fullWidth
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
          </Box>

          <TextField
            label="Endereço"
            fullWidth
            name="address"
            value={formData.address}
            onChange={handleChange}
          />

          <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
            <TextField
              label="Cidade"
              fullWidth
              name="city"
              value={formData.city}
              onChange={handleChange}
            />

            <TextField
              label="Estado"
              fullWidth
              name="state"
              value={formData.state}
              onChange={handleChange}
            />
          </Box>

          <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
            <TextField
              label="CEP"
              fullWidth
              name="zip_code"
              value={formData.zip_code}
              onChange={handleChange}
            />

            <TextField
              label="Data de Nascimento"
              fullWidth
              name="birthDay"
              type="date"
              value={formData.birthDay}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
            <TextField
              label="CPF"
              fullWidth
              name="registrationNumber"
              value={formData.registrationNumber}
              onChange={handleChange}
            />

            <TextField
              label="Telefone"
              fullWidth
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </Box>

          <Button
            variant="contained"
            fullWidth
            sx={{ height: "50px", mt: 2 }}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} color="inherit" />
                Salvando...
              </Box>
            ) : (
              "Salvar alterações"
            )}
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
